import * as turf from '@turf/turf';
import {FeatureCollection} from '@turf/turf';
import {
  getCameraBoundingBox,
  getFontSizeRenderByCanvasRatio,
  toIGNBBoxString,
  updateDataSourceLabelSize
} from '../../cesiumTools';
import {LayerDatasource, LayerLoadType, MapperViewer} from '../../types';

export class WatercourseCIVLayer implements LayerDatasource {

  id = 'watercourse_civ';
  name = 'Cours d\'eau';
  active = true;
  dataSource = new Cesium.GeoJsonDataSource(this.name);
  loadType = LayerLoadType.Manual;
  viewer: MapperViewer;

  /* Custom properties */
  url = 'https://mapserver-v2.predictservices.ws/wfs?\n' +
    'SERVICE=WFS&REQUEST=GetFeature&VERSION=2.0.0&TYPENAMES=ms:civ_cours_d_eau_decoupe_wfs&STARTINDEX=0&C\n' +
    'OUNT=10000&SRSNAME=urn:ogc:def:crs:EPSG::4326&OUTPUTFORMAT=geojson&BBOX=';

  hasLabel = true;
  labelVisible = true;
  labelDistance = 1500;
  geojson: FeatureCollection = null;
  isLoading = false;

  initialize(viewer: MapperViewer): void {
    this.viewer = viewer;
    viewer.dataSources.add(this.dataSource);

    this.viewer.scene.canvas.addEventListener('webglcontextlost', (event) => {
      console.warn('Contexte WebGL perdu. Tentative de réinitialisation...');
      event.preventDefault();
      this.viewer.scene.requestRender();
    });
  }

  remove(): void {
    this.viewer.dataSources.remove(this.dataSource);
  }

  loadData(): void {
    if (!this.dataSource) {
      console.error('Viewer not initialized');
      return;
    }

    const bbox = toIGNBBoxString(getCameraBoundingBox(this.viewer));

    if (bbox) {
      this.isLoading = true;

      const url = `${this.url}${bbox},urn:ogc:def:crs:EPSG::4326`;
      fetch(url)
        .then(res => res.json())
        .then((res: FeatureCollection) => {
          this.geojson = res;

          this.buildRoad(res);
        }).catch(error => {
          console.error('[WatercourseCIVLayer] -> (error) -> ', error);
          this.isLoading = false;
        });
    }
  }

  buildRoad(geojson: FeatureCollection): void {
    this.dataSource.load(geojson, {
      clampToGround: true,
    }).then(() => {
      this.dataSource.entities.values.forEach(road => {
        if (road.polyline) {
          const polyline = road.polyline;
          polyline.material = new Cesium.ColorMaterialProperty(Cesium.Color.ROYALBLUE);
          polyline.width = new Cesium.ConstantProperty(4);
          polyline.arcType = new Cesium.ConstantProperty(Cesium.ArcType.RHUMB);
        }
      });

      this.setLabel(this.labelDistance);
      this.isLoading = false;
    });
  }

  clearLabel(): void {
    this.dataSource.entities.values
      .filter(entity => entity.label)
      .forEach(entity => this.dataSource.entities.remove(entity));
  }

  setLabel(labelDistance): void {
    this.labelDistance = labelDistance;

    this.clearLabel();

    const watercourseMap = new Map();
    const radius = this.labelDistance;

    const textStyle = getFontSizeRenderByCanvasRatio(this.viewer);

    this.geojson?.features?.forEach((watercourse: any) => {

      if (watercourse.geometry.type !== 'LineString') {
        return;
      }

      // On ne prend pas en compte la hauteur (z) des coordonnées
      watercourse.geometry.coordinates = watercourse.geometry.coordinates.map(a => a.slice(0, 2));

      const coordinates = watercourse.geometry.coordinates;
      const watercourseName = watercourse.properties.NOM;

      if (watercourseName !== 'NC') {
        // regroupement des coordonnées (LineString) par nom de cours d'eau pour l'affichage des labels
        if (watercourseMap.has(watercourseName)) {
          watercourseMap.get(watercourseName).push(coordinates);
        } else {
          // On ne prend pas en compte les cours d'eau sans nom
          if (watercourseName) {
            watercourseMap.set(watercourseName, [coordinates]);
          }
        }
      }
    });

    watercourseMap.forEach((lineStringArray, watercourseName) => {
      const filteredLatLngList = lineStringArray
        .flat() // on prend les coordonnées de chaque LineString
        .sort((a, b) => a[0] - b[0]) // on trie par longitude
        .reduce((acc, item) => { // on garde uniquement les points qui sont à plus de (x) mètres de distance
          if (acc.length === 0) {
            acc.push(item);
          } else if (acc.every((a) => turf.rhumbDistance(a, item, {units: 'meters'}) > radius)) {
            acc.push(item);
          }
          return acc;
        }, []);

      // Render des labels
      filteredLatLngList.forEach((coord: number[]) => {
        this.dataSource.entities.add({
          position: Cesium.Cartesian3.fromDegrees(coord[0], coord[1]),
          label: {
            text: watercourseName,
            disableDepthTestDistance: 2000,
            fillColor: Cesium.Color.ROYALBLUE,
            showBackground: true,
            backgroundColor: Cesium.Color.WHITE,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            eyeOffset:  new Cesium.Cartesian3( 0, 0, -200 ),
            ...textStyle
          }
        });
      });
    });
  }

  setActive(active: boolean): void {
    this.active = active;
    this.dataSource.show = active;
  }

  toggleLabels(visible: boolean): void {
    this.labelVisible = !this.labelVisible;
    this.dataSource.entities.values.forEach(entity => {
      if (entity.label) {
        entity.label.show = this.labelVisible;
      }
    });
  }

  onAfterPrint(): void {
    updateDataSourceLabelSize(this.dataSource, this.viewer);
  }

  onBeforePrint(): void {
    updateDataSourceLabelSize(this.dataSource, this.viewer);
  }
}
